import {
  Button,
  ButtonDesigns,
  ButtonTypes,
  Checkbox,
  getFontCss,
  Popup,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import {
  selectPostingOrganizationInvitesFromState,
  selectPostInvitesTopLevelErrorFromState,
} from 'src/app/OrganizationInvites/redux/organizationInvites.selectors'
import ErrorList from 'src/components/ErrorList/ErrorList'
import SvgOrganizationBuildingIcon from 'src/components/Svgs/SvgOrganizationBuildingIcon'
import { useTranslate } from 'src/localization/Trns'
import { PostPeopleInvitesRequest } from 'src/requests/postPeopleInvites.request'
import styled, { css } from 'styled-components'

interface AdminInviteAcknowledgementPopupProps {
  selectedEmailsNotInOrg: string[]
  submitFunction: (
    body: PostPeopleInvitesRequest,
    sendAdminInvite?: boolean,
  ) => void
  closeFunction: (val: boolean) => void
  updateRole?: boolean
}

const StyledDiv = styled.div`
  border-radius: 10px;
  background-color: ${styleConstants.colors.gray_light};
  display: flex;
  padding: 20px;
  flex-direction: column;
  flex: 1 0 0;
  gap: 12px;
`
const AdminInviteAcknowledgementPopup: React.FC<
  AdminInviteAcknowledgementPopupProps
> = (props): React.ReactElement => {
  const postingOrganizationInvites = useSelector(
    selectPostingOrganizationInvitesFromState,
  )
  const translate = useTranslate()
  const topLevelError = useSelector(selectPostInvitesTopLevelErrorFromState)
  const [acknowledged, setAcknowledged] = useState(false)

  return (
    <Popup
      css={css`
        display: flex;
        flex-direction: column;
        z-index: 1;
        width: 770px;
        align-items: flex-start;
        gap: 20px;
        align-self: stretch;
        overflow: hidden;
      `}
      onClickClose={(): void => {
        props.closeFunction(false)
      }}
      showCloseButton
      data-testid="admin-invite-popup"
    >
      <div
        css={css`
          height: 153.037px;
          position: absolute;
          top: 0px;
          right: 0px;
        `}
      >
        <SvgOrganizationBuildingIcon />
      </div>
      <div
        css={css`
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          margin-bottom: 20px;
          ${getFontCss(
            32,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
        `}
      >
        {translate('Confirm Admin Access')}
      </div>
      <div
        css={css`
          display: inline;
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        {translate(
          'Before inviting the following user(s), you must acknowledge the following access that Admin will have:',
        )}
      </div>
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          gap: 20px;
          align-self: stretch;
        `}
      >
        <StyledDiv>
          <span
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )}
            `}
          >
            {translate('I acknowledge that...')}
          </span>
          <span
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )}
            `}
          >
            {translate(
              'Admins will have edit access over all registered accounts. For registered accounts with virtual card capabilities enabled, they will be able to create, update and close virtual cards and budgets.',
            )}
          </span>
        </StyledDiv>
      </div>
      <div
        css={css`
          display: flex;
          flex-direction: row;
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
        `}
      >
        <Checkbox
          css={css`
            margin-right: 0px;
          `}
          isSelected={acknowledged}
          onClick={(): void => setAcknowledged(!acknowledged)}
          testId="admin-invite-checkbox"
        />
        {translate(
          'Please confirm the following user(s) should receive Admin access:',
        )}
      </div>
      <div
        css={css`
          margin-bottom: 20px;
          max-height: 40px;
          text-wrap: wrap;
          overflow-y: auto;
        `}
      >
        {props.selectedEmailsNotInOrg.map((email, i) =>
          i !== props.selectedEmailsNotInOrg.length - 1 ? (
            <span
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )}
              `}
              key={email}
            >
              {`${email}, `}
            </span>
          ) : (
            <span
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )}
              `}
              key={email}
            >
              {email}
            </span>
          ),
        )}
      </div>
      {topLevelError !== undefined && <ErrorList errors={[topLevelError]} />}
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 20px;
        `}
      >
        <Button
          type={ButtonTypes.submit}
          design={ButtonDesigns.designCta}
          big
          disabled={!acknowledged}
          text={translate('Invite as Admin')}
          onClick={(): void =>
            props.submitFunction({ emails: props.selectedEmailsNotInOrg }, true)
          }
          loading={postingOrganizationInvites}
          data-testid="admin-invite-confirm-btn"
        />
        <span
          role="link"
          tabIndex={0}
          onClick={(): void => {
            if (!postingOrganizationInvites) props.closeFunction(false)
          }}
          onKeyDown={undefined}
          // TODO: create a hoverable link design for the red styling
          css={css`
            ${getFontCss(
              14,
              themeConstants.colors.cta,
              styleConstants.fontFamilies.medium,
            )};
            &:hover {
              ${!postingOrganizationInvites &&
              css`
                text-decoration: underline;
                cursor: pointer;
              `}
            }
          `}
          data-testid="MembershipType-CancelButton"
        >
          {translate('Cancel')}
        </span>
      </div>
    </Popup>
  )
}

export default AdminInviteAcknowledgementPopup
